import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CornerLeaves, SectionFlourish, Sprig, HeartDivider } from "@/components/Botanical";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 1.0, ease: [0.16, 1, 0.3, 1] },
};

const BOUQUET_SIZES = [
  { label: "3 piece", price: "Starting at $15.99" },
  { label: "7 piece", price: "Starting at $39.99" },
  { label: "12 piece", price: "Starting at $59.99" },
  { label: "19 piece", price: "Starting at $89.00" },
];

const BOX_SIZES = [
  { label: "4 piece", price: "Starting at $19.99" },
  { label: "6 piece", price: "Starting at $29.99" },
  { label: "12 piece", price: "Starting at $49.99" },
];

const MenuBlock = ({ script, title, items, columns = 2 }) => (
  <motion.section {...fadeUp} className="mb-24 relative" data-testid={`menu-block-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-center mb-10">
      <p className="font-script text-5xl md:text-6xl text-rose leading-none">{script}</p>
      <h2 className="font-display text-3xl md:text-4xl text-ink mt-3">{title}</h2>
      <SectionFlourish className="w-40 h-5 mx-auto mt-5" />
    </div>
    <div className={`grid gap-4 md:gap-6 max-w-4xl mx-auto ${columns === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"}`}>
      {items}
    </div>
  </motion.section>
);

const SizeCard = ({ label, price }) => (
  <div className="frame-gold p-6 text-center bg-ivory">
    <p className="font-display text-2xl text-ink">{label}</p>
    <div className="hairline w-12 mx-auto my-3" />
    <p className="overline text-rose">{price}</p>
  </div>
);

const FlavorPill = ({ name, premium = false }) => (
  <div className={`text-center py-3 px-4 border ${premium ? "border-gold bg-butter/40" : "border-hairline bg-ivory"}`}>
    <p className={`font-display text-lg ${premium ? "text-ink" : "text-ink"}`}>{name}</p>
    {premium && <p className="overline text-gold mt-1 text-[9px]">Premium · +$2 per 6pc</p>}
  </div>
);

export default function Menu() {
  const [settings, setSettings] = useState(null);
  const [addOns, setAddOns] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/settings"), api.get("/add-ons")])
      .then(([s, a]) => { setSettings(s.data); setAddOns(a.data); })
      .catch(() => {});
  }, []);

  const cake = settings?.cake_flavors || [];
  const premium = settings?.premium_flavors || [];
  const filling = settings?.filling_flavors || [];

  return (
    <div className="bg-ivory min-h-screen">
      <Navbar logoUrl={settings?.logo_url} />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-20 bg-watercolor-soft relative overflow-hidden" data-testid="menu-hero">
        <CornerLeaves className="absolute top-24 left-4 w-32 md:w-44 opacity-70" />
        <CornerLeaves className="absolute bottom-4 right-4 w-32 md:w-44 opacity-70" flip />
        <div className="max-w-4xl mx-auto text-center relative">
          <p className="overline text-rose mb-4">The Full Menu</p>
          <p className="font-pinyon text-6xl md:text-8xl text-rose leading-[0.85]">Our Menu</p>
          <SectionFlourish className="w-48 h-6 mx-auto mt-6" />
          <p className="mt-6 font-display italic text-xl md:text-2xl text-ink-soft leading-relaxed max-w-2xl mx-auto">
            Every bouquet, every flavor, every detail — hand-crafted to order.
          </p>
          <Link to="/inquiry" data-testid="menu-inquiry-btn" className="btn-pill-pink mt-10 inline-flex">
            <Sparkles size={14} strokeWidth={1.5} /> Begin Your Inquiry
          </Link>
        </div>
      </section>

      <HeartDivider className="w-64 h-5 mx-auto my-12" />

      <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20 pb-20">
        {/* Bouquets */}
        <MenuBlock
          script="Cupcake Bouquets"
          title="The Signature Arrangement"
          items={BOUQUET_SIZES.map((s, i) => <SizeCard key={i} {...s} />)}
        />

        {/* Cupcakes in Boxes */}
        <MenuBlock
          script="Boxed Florals"
          title="Cupcakes in a Box"
          items={BOX_SIZES.map((s, i) => <SizeCard key={i} {...s} />)}
        />

        <div className="text-center -mt-16 mb-24">
          <p className="text-sm text-ink-soft italic">Add $2 – $10 for clear gift boxes · see Add-ons below.</p>
        </div>

        {/* Cake flavors */}
        <MenuBlock
          script="Cake Flavors"
          title="Classic Selection"
          columns={cake.length + premium.length > 6 ? 3 : 2}
          items={
            <>
              {cake.map((f, i) => <FlavorPill key={`c-${i}`} name={f} />)}
              {premium.map((f, i) => <FlavorPill key={`p-${i}`} name={f} premium />)}
            </>
          }
        />

        {/* Fillings */}
        <MenuBlock
          script="Fillings"
          title="Choose Your Center"
          columns={3}
          items={filling.map((f, i) => <FlavorPill key={i} name={f} />)}
        />

        {/* Add-ons */}
        {addOns.length > 0 && (
          <MenuBlock
            script="Add-ons"
            title="Extra Touches"
            items={addOns.map((a) => (
              <div key={a.id} className="frame-gold p-6 bg-ivory text-center" data-testid={`menu-addon-${a.id}`}>
                {a.image_url && (
                  <div className="img-hover aspect-square mb-4 max-w-[180px] mx-auto">
                    <img src={a.image_url} alt={a.name} />
                  </div>
                )}
                <p className="font-script text-2xl text-rose leading-none">{a.name}</p>
                <Sprig className="w-14 h-5 mx-auto my-3 opacity-70" />
                <p className="text-sm text-ink-soft font-light leading-relaxed">{a.description}</p>
                {a.price && <p className="mt-4 font-display text-xl text-rose">{a.price}</p>}
              </div>
            ))}
          />
        )}

        {/* Final CTA */}
        <motion.div {...fadeUp} className="text-center mt-16 frame-gold p-10 md:p-14 bg-cream-soft">
          <p className="font-pinyon text-5xl md:text-6xl text-rose leading-none">Ready to Begin?</p>
          <HeartDivider className="w-40 h-5 mx-auto my-8" />
          <p className="font-display italic text-lg md:text-xl text-ink-soft max-w-xl mx-auto">
            Every order is a conversation. Share your vision — we'll bring it to life, petal by petal.
          </p>
          <Link to="/inquiry" data-testid="menu-cta-inquiry" className="btn-pill-pink mt-10 inline-flex">
            <Sparkles size={14} strokeWidth={1.5} /> Submit an Inquiry
          </Link>
          <p className="mt-6 text-xs text-ink-soft font-light italic">
            The final price will be worked out individually with you.
          </p>
        </motion.div>
      </div>

      <Footer settings={settings} />
    </div>
  );
}
